import { Comparable } from "list";
import { Ord } from "./List";
import { OrdInternal, toInternalOrd } from "./Ord";

// <https://github.com/fantasyland/fantasy-land>

interface ComparablePair extends OrdInternal {
  e1: Comparable;
  e2: Comparable;
  "fantasy-land/equals": (x: any) => boolean;
  "fantasy-land/lte": (x: any) => boolean;
}

export function comparable<S1 extends Ord, S2 extends Ord>(e1: S1, e2: S2): ComparablePair;
export function comparable<S1 extends Ord, S2 extends Ord, S3 extends Ord>(
  e1: S1,
  e2: S2,
  e3: S3
): ComparablePair;
export function comparable<S1 extends Ord, S2 extends Ord, S3 extends Ord, S4 extends Ord>(
  e1: S1,
  e2: S2,
  e3: S3,
  e4: S4
): ComparablePair;

export function comparable(e1: Ord, e2: Ord, e3?: Ord, e4?: Ord): ComparablePair {
  if (e3 !== undefined) {
    if (e4 !== undefined) {
      return comparable(e1, comparable(e2, comparable(e3, e4)));
    } else {
      return comparable(e1, comparable(e2, e3));
    }
  }

  const eo1 = toInternalOrd(e1);
  const eo2 = toInternalOrd(e2);

  return {
    e1: eo1,
    e2: eo2,
    "fantasy-land/equals": (other: any): boolean => {
      const a = compare(eo1, other.e1);
      if (a !== 0) {
        return false;
      } else {
        return compare(eo2, other.e2) === 0;
      }
    },
    "fantasy-land/lte": (other: any): boolean => {
      const a = compare(eo1, other.e1);
      if (a < 0) {
        return true;
      } else if (a > 0) {
        return false;
      } else {
        return compare(eo2, other.e2) <= 0;
      }
    },
  };
}

/**
 * @param a Must be an instance of `Comparable`
 * @param b Must be an instance of `Comparable`
 * @returns -1, 0, 1
 */
function compare(a: any, b: any): number {
  if (typeof a === "string" || typeof a === "number") {
    if (a > b) {
      return 1;
    } else if (a === b) {
      return 0;
    } else {
      return -1;
    }
  } else {
    if (!a["fantasy-land/lte"](b)) {
      return 1;
    } else if (a["fantasy-land/equals"](b)) {
      return 0;
    } else {
      return -1;
    }
  }
}

export function reverseOrd(x: Ord): Ord {
  const o = toInternalOrd(x);

  if (typeof o === "number") {
    return -o;
  }

  if (typeof o === "string") {
    return <any>{
      v: o,
      "fantasy-land/lte": (b: any): boolean => {
        return b.v <= o;
      },
      "fantasy-land/equals": (b: any): boolean => {
        return b.v === o;
      },
    };
  }

  return {
    "fantasy-land/lte": (b: any): boolean => {
      return b["fantasy-land/lte"](o);
    },
    "fantasy-land/equals": (b: any): boolean => {
      return b["fantasy-land/equals"](o);
    },
  };
}

export function ordWithNullsFirst(_x: Ord | null): Ord {
  throw new Error("TODO !!!");
}

export function ordWithNullsLast(_x: Ord | null): Ord {
  throw new Error("TODO !!!");
}
